import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Calendar, Zap, CreditCard, Gamepad2, Check, ChevronRight } from 'lucide-react';
import { isActivityBlockDone } from '../../utils/enthusiasmDaily';

/** ألوان عصرية لكل بطاقة — حالة عادية / مكتمل */
const CARD_THEME = {
  dailyExercises: {
    idle: 'border-cyan-200/80 bg-gradient-to-br from-cyan-50 via-sky-50/90 to-white hover:border-cyan-400 shadow-[0_8px_0_0_rgba(6,182,212,0.12)] hover:shadow-[0_10px_0_0_rgba(6,182,212,0.18)] focus-visible:ring-cyan-400/50',
    done: 'border-emerald-400 bg-gradient-to-br from-emerald-100/95 via-teal-50/90 to-white shadow-[0_8px_0_0_rgba(16,185,129,0.2)] hover:shadow-[0_10px_0_0_rgba(16,185,129,0.25)] focus-visible:ring-emerald-400/50',
    iconIdle:
      'border-cyan-200/90 bg-gradient-to-br from-cyan-400 to-sky-600 text-white shadow-lg shadow-cyan-500/25',
    iconDone:
      'border-emerald-500 bg-gradient-to-br from-emerald-400 to-teal-600 text-white shadow-lg shadow-emerald-500/30',
    titleDone: 'text-emerald-950',
    chevron: 'text-cyan-600 group-hover:text-cyan-700',
    chevronDone: 'text-emerald-700',
    points: 'text-slate-800',
    pointsDone: 'text-emerald-900',
    listBorder: 'border-cyan-100/90',
    listBorderDone: 'border-emerald-200/80',
    ctaIdle: 'border-cyan-200/90 bg-gradient-to-r from-cyan-50 to-sky-50 text-cyan-950',
    ctaDone: 'border-emerald-400 bg-gradient-to-r from-emerald-100 to-teal-50 text-emerald-950',
  },
  flashAnzan: {
    idle: 'border-violet-200/80 bg-gradient-to-br from-violet-50 via-indigo-50/80 to-white hover:border-violet-400 shadow-[0_8px_0_0_rgba(139,92,246,0.12)] hover:shadow-[0_10px_0_0_rgba(139,92,246,0.18)] focus-visible:ring-violet-400/50',
    done: 'border-indigo-400 bg-gradient-to-br from-indigo-100/95 via-violet-50/90 to-white shadow-[0_8px_0_0_rgba(99,102,241,0.22)] hover:shadow-[0_10px_0_0_rgba(99,102,241,0.28)] focus-visible:ring-indigo-400/50',
    iconIdle:
      'border-violet-200/90 bg-gradient-to-br from-violet-500 to-indigo-600 text-white shadow-lg shadow-violet-500/30',
    iconDone:
      'border-indigo-500 bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-lg shadow-indigo-500/35',
    titleDone: 'text-indigo-950',
    chevron: 'text-violet-600 group-hover:text-violet-700',
    chevronDone: 'text-indigo-700',
    points: 'text-slate-800',
    pointsDone: 'text-indigo-950',
    listBorder: 'border-violet-100/90',
    listBorderDone: 'border-indigo-200/80',
    ctaIdle: 'border-violet-200/90 bg-gradient-to-r from-violet-50 to-indigo-50 text-violet-950',
    ctaDone: 'border-indigo-400 bg-gradient-to-r from-indigo-100 to-violet-50 text-indigo-950',
  },
  flashCard: {
    idle: 'border-rose-200/80 bg-gradient-to-br from-rose-50 via-fuchsia-50/70 to-white hover:border-rose-400 shadow-[0_8px_0_0_rgba(244,114,182,0.12)] hover:shadow-[0_10px_0_0_rgba(244,114,182,0.18)] focus-visible:ring-rose-400/50',
    done: 'border-fuchsia-400 bg-gradient-to-br from-fuchsia-100/95 via-rose-50/90 to-white shadow-[0_8px_0_0_rgba(217,70,239,0.2)] hover:shadow-[0_10px_0_0_rgba(217,70,239,0.26)] focus-visible:ring-fuchsia-400/50',
    iconIdle:
      'border-rose-200/90 bg-gradient-to-br from-rose-400 to-fuchsia-600 text-white shadow-lg shadow-rose-500/25',
    iconDone:
      'border-fuchsia-500 bg-gradient-to-br from-fuchsia-500 to-rose-600 text-white shadow-lg shadow-fuchsia-500/30',
    titleDone: 'text-fuchsia-950',
    chevron: 'text-rose-600 group-hover:text-rose-700',
    chevronDone: 'text-fuchsia-700',
    points: 'text-slate-800',
    pointsDone: 'text-fuchsia-950',
    listBorder: 'border-rose-100/90',
    listBorderDone: 'border-fuchsia-200/80',
    ctaIdle: 'border-rose-200/90 bg-gradient-to-r from-rose-50 to-fuchsia-50 text-rose-950',
    ctaDone: 'border-fuchsia-400 bg-gradient-to-r from-fuchsia-100 to-rose-50 text-fuchsia-950',
  },
  games: {
    idle: 'border-amber-200/80 bg-gradient-to-br from-amber-50 via-orange-50/80 to-white hover:border-amber-400 shadow-[0_8px_0_0_rgba(251,191,36,0.14)] hover:shadow-[0_10px_0_0_rgba(251,191,36,0.2)] focus-visible:ring-amber-400/50',
    done: 'border-orange-400 bg-gradient-to-br from-orange-100/95 via-amber-50/90 to-white shadow-[0_8px_0_0_rgba(249,115,22,0.22)] hover:shadow-[0_10px_0_0_rgba(249,115,22,0.28)] focus-visible:ring-orange-400/50',
    iconIdle:
      'border-amber-200/90 bg-gradient-to-br from-amber-400 to-orange-600 text-white shadow-lg shadow-amber-500/25',
    iconDone:
      'border-orange-500 bg-gradient-to-br from-orange-500 to-amber-600 text-white shadow-lg shadow-orange-500/35',
    titleDone: 'text-orange-950',
    chevron: 'text-amber-600 group-hover:text-amber-700',
    chevronDone: 'text-orange-700',
    points: 'text-slate-800',
    pointsDone: 'text-orange-950',
    listBorder: 'border-amber-100/90',
    listBorderDone: 'border-orange-200/80',
    ctaIdle: 'border-amber-200/90 bg-gradient-to-r from-amber-50 to-orange-50 text-amber-950',
    ctaDone: 'border-orange-400 bg-gradient-to-r from-orange-100 to-amber-50 text-orange-950',
  },
};

const ACTIVITY_CONFIG = [
  {
    key: 'dailyExercises',
    Icon: Calendar,
    i18nLabel: 'navigation.dailyExercises',
    href: '/student/daily-exercises',
  },
  {
    key: 'flashAnzan',
    Icon: Zap,
    i18nLabel: 'navigation.flashAnzan',
    href: '/student/flash-anzan',
  },
  {
    key: 'flashCard',
    Icon: CreditCard,
    i18nLabel: 'navigation.flashCards',
    href: '/student/flash-cards',
  },
  {
    key: 'games',
    Icon: Gamepad2,
    i18nLabel: 'navigation.games',
    href: '/student/games',
  },
];

function bonusField(block, activityKey) {
  if (!block) return null;
  if (activityKey === 'dailyExercises') return block.performanceBonus;
  if (activityKey === 'games') return block.thresholdBonus;
  return block.perfectBonus;
}

function collectDetailLines(block, activityKey, t) {
  if (!block || typeof block !== 'object') return [];
  const lines = [];
  const n = (v) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : null;
  };

  const part = n(block.participation);
  if (part != null && part !== 0) {
    lines.push(t('dashboard.student.enthusiasmDetailParticipation', { value: part }));
  }

  const bonus = n(bonusField(block, activityKey));
  if (bonus != null && bonus !== 0) {
    if (activityKey === 'dailyExercises') {
      lines.push(t('dashboard.student.enthusiasmDetailPerfBonus', { value: bonus }));
    } else if (activityKey === 'games') {
      lines.push(t('dashboard.student.enthusiasmDetailThresholdBonus', { value: bonus }));
    } else {
      lines.push(t('dashboard.student.enthusiasmDetailPerfectBonus', { value: bonus }));
    }
  }

  const extra = n(block.extraSessionsPoints);
  if (extra != null && extra !== 0) {
    lines.push(t('dashboard.student.enthusiasmDetailExtra', { value: extra }));
  }

  const sessions = n(block.sessionCount);
  if (sessions != null && sessions !== 0) {
    lines.push(t('dashboard.student.enthusiasmDetailSessions', { count: sessions }));
  }

  if (activityKey === 'dailyExercises' && block.bestRatio != null && block.bestRatio !== '') {
    lines.push(t('dashboard.student.enthusiasmDetailBestRatio', { ratio: String(block.bestRatio) }));
  }

  if (activityKey === 'flashCard' && block.details?.mode) {
    const rawMode = String(block.details.mode).trim().toLowerCase();
    const modeLabel =
      rawMode === 'soroban_only' ? t('dashboard.student.enthusiasmFlashModeAbacusOnly') : String(block.details.mode);
    lines.push(t('dashboard.student.enthusiasmDetailMode', { mode: modeLabel }));
  }

  const exercises = block.details?.exercises;
  if (activityKey === 'dailyExercises' && Array.isArray(exercises) && exercises.length > 0) {
    lines.push(t('dashboard.student.enthusiasmDetailExerciseCount', { count: exercises.length }));
  }

  const sess = block.details?.sessions;
  if (Array.isArray(sess) && sess.length > 0 && activityKey !== 'dailyExercises') {
    lines.push(t('dashboard.student.enthusiasmDetailSessionList', { count: sess.length }));
  }

  return lines;
}

/**
 * مسار أنشطة اليوم — بطاقات كبيرة، تفاصيل من الـ API، رابط لكل نشاط.
 */
const EnthusiasmDailyPath = ({ breakdown, loading }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <section className="w-full" aria-busy="true">
        <div className="mb-6 h-10 w-72 max-w-full animate-pulse rounded-xl bg-gradient-to-r from-slate-200 to-slate-100" />
        <div className="grid grid-cols-2 gap-2 sm:gap-4 lg:gap-6">
          {[
            'from-cyan-100/40 to-sky-50/50 border-cyan-200/40',
            'from-violet-100/40 to-indigo-50/50 border-violet-200/40',
            'from-rose-100/40 to-fuchsia-50/50 border-rose-200/40',
            'from-amber-100/40 to-orange-50/50 border-amber-200/40',
          ].map((cls, i) => (
            <div
              key={i}
              className={`min-h-[11rem] rounded-xl border-[3px] bg-gradient-to-br ${cls} animate-pulse sm:min-h-[14rem] sm:rounded-2xl`}
            />
          ))}
        </div>
      </section>
    );
  }

  if (!breakdown) {
    return null;
  }

  return (
    <section className="w-full" aria-label={t('dashboard.student.enthusiasmPathTitle')}>
      <div className="relative mb-6 sm:mb-8">
        <div className="pointer-events-none absolute -left-4 top-1/2 h-px w-12 -translate-y-1/2 bg-gradient-to-r from-transparent to-violet-400/60 sm:w-16" aria-hidden />
        <p className="mb-1 text-[0.65rem] font-black uppercase tracking-[0.2em] text-violet-600 sm:text-xs">
          {t('dashboard.myProgress')}
        </p>
        <h2 className="bg-gradient-to-r from-slate-900 via-violet-800 to-fuchsia-700 bg-clip-text text-2xl font-black tracking-tight text-transparent sm:text-3xl">
          {t('dashboard.student.enthusiasmPathTitle')}
        </h2>
        <p className="mt-2 max-w-3xl text-sm font-semibold leading-relaxed text-slate-600 sm:text-[15px]">
          {t('dashboard.student.enthusiasmPathSubtitle')}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:gap-4 lg:gap-6">
        {ACTIVITY_CONFIG.map(({ key, Icon, i18nLabel, href }) => {
          const block = breakdown[key];
          const done = isActivityBlockDone(block);
          const subtotal = block?.subtotal != null ? Number(block.subtotal) : 0;
          const detailLines = collectDetailLines(block, key, t);
          const theme = CARD_THEME[key] || CARD_THEME.games;

          return (
            <Link
              key={key}
              to={href}
              className={`group relative flex h-full min-h-[13rem] flex-col rounded-xl border-[3px] p-2.5 shadow-[0_6px_0_0_rgba(15,23,42,0.06)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_0_0_rgba(15,23,42,0.06)] focus:outline-none focus-visible:ring-4 sm:min-h-[16rem] sm:rounded-[1.35rem] sm:p-5 ${
                done ? theme.done : theme.idle
              }`}
            >
              <div className="mb-2 flex items-center gap-1.5 sm:gap-2.5">
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border-2 sm:h-11 sm:w-11 sm:rounded-xl sm:border-[3px] md:h-12 md:w-12 md:rounded-2xl ${
                    done ? theme.iconDone : theme.iconIdle
                  }`}
                >
                  {done ? (
                    <Check className="h-[1.125rem] w-[1.125rem] stroke-[3] sm:h-5 sm:w-5 md:h-6 md:w-6" strokeWidth={2.5} aria-hidden />
                  ) : (
                    <Icon
                      className="h-[1.125rem] w-[1.125rem] opacity-95 drop-shadow-sm sm:h-5 sm:w-5 md:h-6 md:w-6"
                      strokeWidth={2.25}
                      aria-hidden
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1 text-start">
                  <h3
                    className={`line-clamp-2 text-[11px] font-black leading-snug sm:text-xs md:text-sm ${
                      done ? theme.titleDone : 'text-slate-900'
                    }`}
                  >
                    {t(i18nLabel)}
                  </h3>
                  <p
                    className={`mt-0.5 text-sm font-black tabular-nums leading-none sm:mt-0.5 sm:text-base md:text-lg ${
                      done ? theme.pointsDone : theme.points
                    }`}
                  >
                    {t('dashboard.student.enthusiasmActivityPoints', { points: subtotal })}
                  </p>
                </div>
                <ChevronRight
                  className={`h-4 w-4 shrink-0 opacity-0 transition-all duration-200 group-hover:opacity-100 sm:h-[1.125rem] sm:w-[1.125rem] md:h-5 md:w-5 ${
                    done ? theme.chevronDone : theme.chevron
                  }`}
                  aria-hidden
                />
              </div>

              <ul className="mt-auto flex min-h-0 flex-1 flex-col space-y-0.5 text-[10px] font-semibold leading-snug text-slate-600 sm:min-h-[2.75rem] sm:space-y-1 sm:text-xs md:min-h-[3rem]">
                {detailLines.length > 0 ? (
                  detailLines.map((line, idx) => (
                    <li
                      key={idx}
                      className={`border-b border-dashed pb-1 leading-snug last:border-0 ${
                        done ? theme.listBorderDone : theme.listBorder
                      }`}
                    >
                      {line}
                    </li>
                  ))
                ) : (
                  <li className="font-medium text-slate-500">{t('dashboard.student.enthusiasmCardNoDetails')}</li>
                )}
              </ul>

              <div
                className={`mt-3 flex items-center justify-between rounded-xl border-[2px] px-2.5 py-1.5 text-[11px] font-extrabold sm:mt-4 sm:rounded-2xl sm:px-3 sm:py-2 sm:text-sm ${
                  done ? theme.ctaDone : theme.ctaIdle
                }`}
              >
                <span>{t('dashboard.student.enthusiasmCardCta')}</span>
                <ChevronRight className="h-3.5 w-3.5 shrink-0 rtl:rotate-180 sm:h-4 sm:w-4" aria-hidden />
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export default EnthusiasmDailyPath;
